import { useState } from 'react';
import { Ellipsis } from 'lucide-react';
import { RowWithId } from '@/components/table/types/RowWithId.ts';
import {
  isCustomMenuItem,
  RowContextMenuItemParams,
} from '@/components/table/components/RowContextMenu.tsx';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/Drawer.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { cn } from '@/lib/utils.ts';

/**
 * Menu wiersza na telefonie: te same akcje co w rozwijanym menu tabeli, tylko
 * w arkuszu od dołu — lista pozycji szerokości ekranu trafia się kciukiem,
 * a menu kontekstowe przy krawędzi karty nie.
 */
const MobileRowActions = <T extends RowWithId>({
  rowData,
  items,
}: {
  rowData: T;
  items: RowContextMenuItemParams<T>[];
}) => {
  const [open, setOpen] = useState(false);

  const visibleItems = items.filter(
    (item) => item.visible === undefined || item.visible(rowData),
  );

  if (visibleItems.length === 0) return null;

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <ActionButton
          tone="ghost"
          size="xs"
          iconOnly
          aria-label="Akcje wiersza"
          // Arkusz ukrywa resztę strony przez `aria-hidden`; przycisk nie może
          // wtedy trzymać fokusu, bo przeglądarka zgłasza ukrycie aktywnego elementu.
          onClick={(event) => event.currentTarget.blur()}
          className="shrink-0 text-neutral-600"
        >
          <Ellipsis aria-hidden />
        </ActionButton>
      </DrawerTrigger>
      <DrawerContent
        aria-describedby={undefined}
        className="rounded-t-[22px] border-t-0 bg-basic-white"
      >
        <DrawerTitle className="sr-only">Akcje wiersza</DrawerTitle>
        <ul className="flex flex-col gap-0.5 px-2 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2">
          {visibleItems.map((item, index) => {
            const disabled = item.disabled?.(rowData) ?? false;
            return (
              <li key={index}>
                <button
                  type="button"
                  disabled={disabled}
                  title={disabled ? item.disabledTooltip?.(rowData) : undefined}
                  onClick={() => {
                    item.action(rowData);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center rounded-md px-3 py-3 text-left text-sm font-medium text-base-text hover:bg-neutral-50 disabled:opacity-50',
                    !isCustomMenuItem(item) &&
                      item.variant === 'destructive' &&
                      'text-error-200',
                  )}
                >
                  {isCustomMenuItem(item)
                    ? item.customRender(rowData)
                    : item.label}
                </button>
              </li>
            );
          })}
        </ul>
      </DrawerContent>
    </Drawer>
  );
};

export default MobileRowActions;
